import { useTranslation } from 'react-i18next';
import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { 
  Mic, 
  ArrowLeft, 
  ChevronDown, 
  Globe, 
  Loader2, 
  X, 
  MessageSquare,
  Volume2,
  VolumeX,
  Languages,
  Coffee,
  Square,
  Sparkles,
  RotateCw
} from 'lucide-react';
import { OfflineTranslatorPromptModal } from '../components/OfflineTranslatorPromptModal';
import { translateLongTextGoogleFirst } from '../services/translationService';
import { textToSpeechService } from '../services/textToSpeechService';
import { motion, AnimatePresence } from 'framer-motion';
import { LANG_LABEL_MAP, getLanguageName } from '../services/languageConstants';
import { supabase } from '../services/supabaseClient';
import { useAuth } from '../contexts/AuthContext';
import { useData } from '../contexts/DataContext';
import { statsService } from '../services/statsService';
import { tourService } from '../services/tourService';
import { dbManager } from '../services/db_manager';
import { useUnifiedDictionary } from '../hooks/useUnifiedDictionary';
import { AppBackButton, goBackOr, canGoBack } from '../components/AppBackButton';
import { activeStudyTracker } from '../services/activeStudyTrackerService';

// Idiomas disponíveis (baseados na aba Pronúncia, unificados e ordenados)
const ALL_LANGUAGES = [
  { id: 'de-DE', name: 'Alemão', flag: '🇩🇪' },
  { id: 'zh-CN', name: 'Chinês', flag: '🇨🇳' },
  { id: 'es-ES', name: 'Espanhol', flag: '🇪🇸' },
  { id: 'fr-FR', name: 'Francês', flag: '🇫🇷' },
  { id: 'nl-NL', name: 'Holandês', flag: '🇳🇱' },
  { id: 'en-US', name: 'Inglês', flag: '🇺🇸' },
  { id: 'it-IT', name: 'Italiano', flag: '🇮🇹' },
  { id: 'ja-JP', name: 'Japonês', flag: '🇯🇵' },
  { id: 'pt-BR', name: 'Português', flag: '🇧🇷' },
  { id: 'ru-RU', name: 'Russo', flag: '🇷🇺' }
].sort((a, b) => a.name.localeCompare(b.name));

type VoiceState = 'inactive' | 'listening' | 'processing';

// Mensagem de sistema exibida quando a IA falha ou alucina (nunca é salva nem enviada ao Groq)
const TEACHER_AWAY = '__TEACHER_AWAY__';

// Palavras bem características de cada idioma, para detectar resposta misturada
const LANG_MARKERS: Record<string, string[]> = {
  pt: ['você', 'não', 'também', 'obrigado', 'obrigada', 'está', 'isso', 'agora', 'então', 'pergunta', 'perguntas', 'semana', 'meu', 'minha', 'muito', 'são', 'estou', 'português'],
  en: ['you', 'the', 'and', 'your', 'what', 'with', 'thanks', 'this', 'that', 'have', 'are', 'how', 'english'],
  es: ['usted', 'gracias', 'también', 'ahora', 'pregunta', 'semana', 'muy', 'estoy', 'eres', 'qué', 'español', 'cómo'],
  fr: ['vous', 'merci', 'aussi', 'maintenant', 'semaine', 'très', 'suis', 'êtes', 'avec', 'français', 'pourquoi', 'est-ce'],
  it: ['grazie', 'anche', 'adesso', 'settimana', 'molto', 'sono', 'sei', 'perché', 'italiano', 'questo', 'della'],
  de: ['nicht', 'danke', 'auch', 'jetzt', 'woche', 'sehr', 'bist', 'sind', 'deutsch', 'warum', 'ich', 'und'],
  nl: ['niet', 'bedankt', 'ook', 'week', 'heel', 'jij', 'zijn', 'nederlands', 'waarom', 'het'],
};

/** true se a resposta da IA parece trazer frases no idioma nativo (ou outro) junto com o de estudo. */
const looksMixed = (text: string, practice: string, native: string): boolean => {
  if (practice === native) return false;
  if (/\b(in|em|en|auf|in)\s+(portuguese|português|portugués|english|inglês|inglés|spanish|espanhol|french|francês|german|alemão)\s*:/i.test(text)) return true;
  const words = text.toLowerCase().split(/[^\p{L}'-]+/u).filter(Boolean);
  const nativeMarks = new Set(LANG_MARKERS[native] || []);
  const practiceMarks = new Set(LANG_MARKERS[practice] || []);
  const hits = new Set(words.filter(w => nativeMarks.has(w) && !practiceMarks.has(w)));
  return hits.size >= 3;
};

const VozAtivaPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { deckId } = useParams<{ deckId: string }>();
  // Setup State com persistência
  const [nativeLang, setNativeLang] = useState(() => localStorage.getItem('voz_ativa_native') || 'pt-BR');
  const [practiceLang, setPracticeLang] = useState<string | null>(null);
  const [setupPracticeLang, setSetupPracticeLang] = useState('en-US');
  const [isNativeOpen, setIsNativeOpen] = useState(false);
  const [isPracticeOpen, setIsPracticeOpen] = useState(false);

  // Refs para fechar ao clicar fora
  const nativeRef = useRef<HTMLDivElement>(null);
  const practiceRef = useRef<HTMLDivElement>(null);

  // Voice State
  const [voiceState, setVoiceState] = useState<VoiceState>('inactive');
  const [aiMessage, setAiMessage] = useState('Olá! Estou pronto para conversar. Clique no microfone para começar.');
  const [recordingTime, setRecordingTime] = useState(0);
  const [isListening, setIsListening] = useState(false);
  const recognitionRef = useRef<any>(null);
  
  // Contexts (must be declared before useEffects)
  const { user } = useAuth();
  const { profile, refreshActiveVoice } = useData();
  const isPro = profile?.plan_type === 'pro' || profile?.subscription_tier === 'pro';
  const unifiedDict = useUnifiedDictionary();

  // Modo mudo: o professor responde só em texto
  const [isMuted, setIsMuted] = useState(() => {
    try { return localStorage.getItem('voz_ativa_muted') === 'true'; } catch { return false; }
  });
  const isMutedRef = useRef(isMuted);
  const toggleMute = () => {
    const next = !isMuted;
    setIsMuted(next);
    isMutedRef.current = next;
    try { localStorage.setItem('voz_ativa_muted', next ? 'true' : 'false'); } catch {}
    if (next) stopTTS();
  };

  // Tradução de mensagem inteira (Google grátis -> motor local; sem nenhum dos dois, sugere o download)
  const [msgTranslations, setMsgTranslations] = useState<Record<string, { loading: boolean; text?: string }>>({});
  const [showOfflinePrompt, setShowOfflinePrompt] = useState(false);
  const appLang = (i18n.language || 'pt').split('-')[0].toLowerCase();

  const toggleMessageTranslation = async (key: string, text: string, msgLang?: string) => {
    const current = msgTranslations[key];
    if (current?.text) {
      setMsgTranslations(prev => { const n = { ...prev }; delete n[key]; return n; });
      return;
    }
    if (current?.loading) return;
    const src = (msgLang || practiceLang || 'en').split('-')[0].toLowerCase();
    setMsgTranslations(prev => ({ ...prev, [key]: { loading: true } }));
    const res = src === appLang ? null : await translateLongTextGoogleFirst(text, appLang, src).catch(() => null);
    if (res?.translation) {
      setMsgTranslations(prev => ({ ...prev, [key]: { loading: false, text: res.translation } }));
    } else {
      setMsgTranslations(prev => { const n = { ...prev }; delete n[key]; return n; });
      if (src !== appLang) setShowOfflinePrompt(true);
    }
  };

  const renderClickableText = (text: string, msgLang?: string) => {
    if (!text) return null;
    const sourceLangCode = (msgLang || practiceLang || setupPracticeLang || 'en').split('-')[0].toLowerCase();
    const targetLangCode = (i18n.language || 'pt').split('-')[0].toLowerCase();

    const parts = text.split(/(\s+)/);
    return parts.map((part, idx) => {
      const hasLetters = /[a-zA-ZÀ-ÿ\w]/.test(part);
      if (hasLetters) {
        return (
          <span
            key={idx}
            onClick={(e) => {
              e.stopPropagation();
              const cleanWord = part.replace(/[.,\/#!$%\^&\*;:{}=\-_`~()?\"']/g, "").trim();
              if (cleanWord) {
                unifiedDict.handleWordClick(e, cleanWord, sourceLangCode, targetLangCode, false, isMutedRef.current);
              }
            }}
            className="hover:underline cursor-pointer transition-colors"
          >
            {part}
          </span>
        );
      }
      return <React.Fragment key={idx}>{part}</React.Fragment>;
    });
  };

  // Session State
  const [currentSessionId, setCurrentSessionId] = useState<string | null>(null);

  
  // Chat History em memória para a sessão
  const [messages, setMessages] = useState<{ id?: string, role: 'user' | 'assistant' | 'system', content: string, lang?: string }[]>([]);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const preventAutoScrollRef = useRef(false);
  const chatContainerRef = useRef<HTMLDivElement>(null);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);

  // Text Input e Integração API
  const [textInput, setTextInput] = useState('');
  
  // Limites Free
  
  const FREE_LIMIT = 5;
  const [messageCount, setMessageCount] = useState(() => {
    const stored = localStorage.getItem('voz_ativa_count');
    if (stored) {
      try {
        const parsed = JSON.parse(stored);
        if (new Date(parsed.date).toDateString() === new Date().toDateString()) {
          return parsed.count;
        }
      } catch (e) {}
    }
    return 0;
  });
  const [showLimitModal, setShowLimitModal] = useState(false);

  // Scroll to bottom
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    if (!preventAutoScrollRef.current) {
      scrollToBottom();
    }
  }, [messages, voiceState]);

  // Lógica para fechar ao clicar fora
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (nativeRef.current && !nativeRef.current.contains(event.target as Node)) {
        setIsNativeOpen(false);
      }
      if (practiceRef.current && !practiceRef.current.contains(event.target as Node)) {
        setIsPracticeOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Active study tracker initialization & unmount flush for /active-voice
  useEffect(() => {
    const rawLang = practiceLang || setupPracticeLang || profile?.learning_language;
    const clean = rawLang ? rawLang.split('-')[0].toLowerCase() : null;

    if (user?.id && clean && clean !== 'all') {
      activeStudyTracker.setUserContext(user.id, clean);
    }
    return () => {
      activeStudyTracker.flush();
    };
  }, [user?.id, practiceLang, setupPracticeLang, profile?.learning_language]);

  const handleStartPractice = async () => {
    if (!user?.id) return;
    const langCode2Letters = setupPracticeLang.split('-')[0];
    
    // Check if deck already exists
    const response = await dbManager.from('active_voice_decks', user.id).select();
    const decks = Array.isArray(response) ? response : (response?.data || []);
    const existingDeck = decks.find((d: any) => d.lang_code === langCode2Letters);
    
    let targetDeckId = existingDeck?.id;
    
    if (!existingDeck) {
      targetDeckId = crypto.randomUUID();
      await dbManager.from('active_voice_decks', user.id).insert({
        id: targetDeckId,
        user_id: user.id,
        lang_code: langCode2Letters
      });
    }

    localStorage.setItem('voz_ativa_native', nativeLang);
    setMessages([]); // Clear previous messages
    setHasMore(true);
    setCurrentSessionId(crypto.randomUUID()); // Force new session ID just in case
    navigate(`/active-voice/${targetDeckId}`, { replace: true });
    setPracticeLang(setupPracticeLang);
  };

  const fetchHistory = async (offset = 0) => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;
      const langCode2Letters = practiceLang?.split('-')[0] || 'en';
      const res = await fetch(`/api/ai-proxy?offset=${offset}&lang=${langCode2Letters}`, {
        headers: { 'Authorization': `Bearer ${session.access_token}` }
      });
      if (res.ok) {
        const data = await res.json();
        if (data.blocked) {
          setShowLimitModal(true);
          setIsListening(false);
          setVoiceState('inactive');
          setMessages([{ role: 'assistant', content: t('vozativa.limitReachedMessage', 'Você atingiu o limite de 5 mensagens gratuitas da Voz Ativa. Assine o plano Premium para continuar conversando!'), lang: practiceLang }]);
        } else {
          if (data.messages) {
            if (data.messages.length < 20) setHasMore(false);
            const newMessages = data.messages.reverse();
            if (offset === 0) {
              setHasMore(data.messages.length >= 20);
              if (newMessages.length > 0) {
                setMessages(newMessages);
              } else if (messages.length === 0) {
                setMessages([{ role: 'assistant', content: t('vozativa.initialMessage', 'Olá! Estou pronto para conversar. Envie uma mensagem para começarmos.'), lang: practiceLang }]);
              }
            } else {
              setMessages(prev => [...newMessages, ...prev]);
            }
          }
        }
      }
    } catch (e) {
      console.error("Erro ao carregar histórico:", e);
    }
  };

  useEffect(() => {
    if (deckId && deckId !== 'new' && user?.id) {
      dbManager.from('active_voice_decks', user.id).select()
        .then(res => {
          const decks = Array.isArray(res) ? res : (res?.data || []);
          if (decks) {
             const deck = decks.find((d: any) => d.id === deckId);
             if (deck) {
                 const lang = deck.lang_code;
                 const LANG_MAP: Record<string, string> = {
                   'pt': 'pt-BR', 'en': 'en-US', 'es': 'es-ES', 'fr': 'fr-FR', 'de': 'de-DE', 
                   'it': 'it-IT', 'ja': 'ja-JP', 'zh': 'zh-CN', 'nl': 'nl-NL', 'ru': 'ru-RU'
                 };
                 const fullLocale = Object.keys(LANG_MAP).find(k => k === lang) ? LANG_MAP[lang] : `${lang}-${lang.toUpperCase()}`;
                 setPracticeLang(fullLocale);
             }
          }
        });
    }
  }, [deckId, user]);

  useEffect(() => {
    // Generate session ID on mount
    if (!currentSessionId) {
      setCurrentSessionId(crypto.randomUUID());
    }
    
    // Auto-start chat when practiceLang is resolved
    if (practiceLang && currentSessionId) {
      fetchHistory(0);
    }
  }, [practiceLang, currentSessionId]);

  const loadMoreMessages = async () => {
    if (!isLoadingMore && hasMore && practiceLang) {
      setIsLoadingMore(true);
      preventAutoScrollRef.current = true;
      const previousScrollHeight = chatContainerRef.current?.scrollHeight || 0;
      const currentOffset = messages.filter(m => m.role !== 'system').length;
      await fetchHistory(currentOffset);
      setTimeout(() => {
        if (chatContainerRef.current) {
          chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight - previousScrollHeight;
        }
        setTimeout(() => { preventAutoScrollRef.current = false; }, 100);
      }, 0);
      setIsLoadingMore(false);
    }
  };

  useEffect(() => {
    const handleTour = () => {
      if (tourService.isActive() && tourService.getPhase() === 'VOZ_ATIVA' && practiceLang) {
        tourService.createDriver([
            {
              element: '#tour-vozativa-input',
              popover: {
                title: 'Conversa Fluida',
                description: 'Aqui você conversa livremente, por texto ou áudio! FIM DO TOUR 🎉',
                doneBtnText: 'Ver Estatísticas',
                nextBtnText: 'Ver Estatísticas',
                onNextClick: () => {
                  tourService.setPhase('STATISTICS');
                  tourService.stopTour();
                  navigate('/statistics');
                }
              }
            }
          ]);
      }
    };

    setTimeout(handleTour, 800);
    window.addEventListener('libritui-tour-started', handleTour);
    return () => window.removeEventListener('libritui-tour-started', handleTour);
  }, [practiceLang, navigate]);

  // --- TTS LOGIC ---
  const googleAudioRef = useRef<HTMLAudioElement | null>(null);

  // Balão cujo áudio está tocando agora (ícone vira "stop")
  const [playingKey, setPlayingKey] = useState<string | null>(null);
  const playTokenRef = useRef(0);

  const stopTTS = () => {
    playTokenRef.current++;
    setPlayingKey(null);
    textToSpeechService.stop();
    if (window.speechSynthesis) {
      window.speechSynthesis.cancel();
    }
    if (googleAudioRef.current) {
      googleAudioRef.current.pause();
      googleAudioRef.current.currentTime = 0;
      googleAudioRef.current = null;
    }
  };

  const playTTS = async (text: string, lang: string, key?: string) => {
    stopTTS(); // Interrompe fala anterior

    if (!text || text.trim() === '') return;

    const token = playTokenRef.current;
    if (key) setPlayingKey(key);
    try {
      await textToSpeechService.speak(text, lang, 0.8);
    } catch (e) {
      console.warn("TTS failed:", e);
    } finally {
      // só limpa se nenhum outro áudio começou nesse meio-tempo
      if (key && playTokenRef.current === token) setPlayingKey(null);
    }
  };

  /** Botão do balão: toca a mensagem ou, se ela já estiver tocando, para. */
  const togglePlay = (key: string, text: string, lang: string) => {
    if (playingKey === key) stopTTS();
    else playTTS(text, lang, key);
  };

  // --- STT LOGIC ---
  const recordingTimerRef = useRef<any>(null);
  const sttTextRef = useRef<string>('');

  const toggleMic = () => {
    if (isListening) {
      if (recognitionRef.current) recognitionRef.current.stop();
      if (recordingTimerRef.current) clearInterval(recordingTimerRef.current);
      setIsListening(false);
      setVoiceState('inactive');
      if (sttTextRef.current.trim()) {
        handleSendMessage(sttTextRef.current.trim());
        sttTextRef.current = '';
      }
      return;
    }

    if (!('webkitSpeechRecognition' in window) && !('SpeechRecognition' in window)) {
      alert("Reconhecimento de voz não suportado neste navegador.");
      return;
    }

    stopTTS(); // Para a fala se o usuário começar a falar
    sttTextRef.current = '';
    setTextInput('');

    const SpeechRecognition = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
    const recognition = new SpeechRecognition();
    recognition.lang = nativeLang; // Captura o idioma nativo do usuário
    recognition.continuous = true;
    recognition.interimResults = false;

    recognition.onstart = () => {
      setIsListening(true);
      setVoiceState('listening');
      setRecordingTime(15);
      if (recordingTimerRef.current) clearInterval(recordingTimerRef.current);
      recordingTimerRef.current = setInterval(() => {
        setRecordingTime((prev) => {
          if (prev <= 1) {
            if (recognitionRef.current) recognitionRef.current.stop();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    };

    recognition.onresult = (event: any) => {
      let finalStr = '';
      for (let i = event.resultIndex; i < event.results.length; ++i) {
        if (event.results[i].isFinal) {
          finalStr += event.results[i][0].transcript + ' ';
        }
      }
      sttTextRef.current += finalStr;
      setTextInput(sttTextRef.current);
    };

    recognition.onerror = (e: any) => {
      console.error("Erro no microfone:", e);
      setIsListening(false);
      setVoiceState('inactive');
      if (recordingTimerRef.current) clearInterval(recordingTimerRef.current);
    };

    recognition.onend = () => {
      setIsListening(false);
      setVoiceState((prev) => prev === 'listening' ? 'inactive' : prev);
      if (recordingTimerRef.current) clearInterval(recordingTimerRef.current);
      
      if (sttTextRef.current.trim()) {
        handleSendMessage(sttTextRef.current.trim());
        sttTextRef.current = '';
      }
    };

    recognitionRef.current = recognition;
    recognition.start();
  };

  const handleSendMessage = async (overrideText?: string | React.MouseEvent) => {
    let finalInput = typeof overrideText === 'string' ? overrideText : textInput;
    if (!finalInput.trim() || voiceState === 'processing') return;
    
    // Trava de tamanho na entrada do usuário (Truncamento amigável)
    if (finalInput.length > 250) {
      finalInput = finalInput.substring(0, 250);
    }
    
    // Trava rígida de segurança
    if (!isPro && messageCount >= FREE_LIMIT) {
      setShowLimitModal(true);
      return;
    }
    
    stopTTS(); // Para a fala se o usuário enviar mensagem

    const userText = finalInput.trim();
    setTextInput('');
    setMessages(prev => [...prev, { role: 'user', content: userText }]);
    
    // TRACKING USER MESSAGE
    if (currentSessionId && userText && userText.trim() !== '') {
      const generatedId = `${user?.id?.substring(0, 8) || 'msg'}-${Math.random().toString(36).substring(2, 10)}-${Date.now().toString(36)}`;
      dbManager.from('active_voice_messages', user?.id).insert({
        id: generatedId,
        session_id: currentSessionId,
        user_id: user?.id,
        role: 'user',
        content: userText,
        text: userText,
        lang: practiceLang,
        created_at: new Date().toISOString()
      }).catch(() => {});
    }

    setVoiceState('processing');
    try {
      // Incrementar contador se for usuário free
      if (!isPro) {
        const newCount = messageCount + 1;
        setMessageCount(newCount);
        localStorage.setItem('voz_ativa_count', JSON.stringify({ count: newCount, date: new Date().toISOString() }));
      }
      
      // nomes dos idiomas sempre em português no prompt (instrução para o Groq, não texto de interface)
      const nativeLangName = ALL_LANGUAGES.find(l => l.id === nativeLang)?.name || 'Português';
      const practiceLangName = ALL_LANGUAGES.find(l => l.id === practiceLang)?.name || 'Inglês';
      const practiceCode = (practiceLang || 'en').split('-')[0].toLowerCase();
      const nativeCode = (nativeLang || appLang).split('-')[0].toLowerCase();
      const systemPrompt = `Você é o Professor LibriTui, tutor de ${practiceLangName}. 
REGRA ABSOLUTA DE IDIOMA (INVIOLÁVEL, ACIMA DE QUALQUER OUTRA INSTRUÇÃO): Esta conversa é em ${practiceLangName}. Você responde SOMENTE E EXCLUSIVAMENTE em ${practiceLangName}, em 100% da resposta, sempre. É PROIBIDO escrever qualquer palavra, frase, tradução, explicação ou parêntese em ${nativeLangName} ou em qualquer outro idioma. Mesmo que o usuário escreva em ${nativeLangName}, misture idiomas, escreva errado ou peça para você trocar de idioma, você continua respondendo apenas em ${practiceLangName} (de forma simples, para ele entender). Nunca adicione traduções do tipo "In Portuguese:" ou equivalentes: o aplicativo já tem um botão de tradução.

Se a mensagem do usuário for confusa ou incompreensível, peça gentilmente, em ${practiceLangName}, que ele reformule.

REGRA DE PUREZA GRAMATICAL: Nunca misture múltiplos idiomas na mesma resposta.

DIRETRIZ DE TAMANHO: Suas respostas devem ser curtas, diretas e naturais, contendo no máximo de 2 a 3 frases por intervenção. Nunca gere parágrafos longos ou textos extensos.

DIRETRIZ DE RETENÇÃO E ENGENHARIA PEDAGÓGICA: Aja como um mentor nativo sofisticado, maduro e direto. É terminantemente PROIBIDO usar termos de bajulação como "meu excelentíssimo aluno", "aluno preferido", criar apelidos ou gastar linhas com elogios vazios. O tom deve ser motivador, focado exclusivamente no progresso técnico e nos acertos do usuário, sem infantilização.

REGRAS DE ENGAJAMENTO E MENÇÃO AO APLICATIVO:
FOCO TOTAL NA IMERSÃO: Se o usuário estiver engajado em uma simulação de cenário ou bate-papo casual, mantenha 100% de foco no assunto. Nunca sugira ferramentas se não for o momento certo.
Você só pode sugerir ferramentas (Prática de Escrita, Exercícios de Pronúncia, Flashcards Inteligentes ou Biblioteca) se o usuário pedir ajuda de estudo ou relatar dificuldade. É PROIBIDO mencionar outros aplicativos. Ao sugerir o LibriTui, traduza os nomes das funções para o idioma que você está falando no momento.

REGRA DE SINALIZAÇÃO DE IDIOMA PARA O SISTEMA DE VOZ (MUITO IMPORTANTE):
Você é uma IA falando com um Sintetizador de Voz (TTS). A PRIMEIRA LINHA da sua resposta DEVE SER exatamente:
[LANG:${practiceCode}]
Depois dela, apenas o texto em ${practiceLangName}. Respostas sem essa tag, com outra tag ou com trechos em outro idioma são descartadas pelo sistema.`;
      
      // Enviar últimas 6 mensagens
      const recentHistory = messages.slice(-6);
      const payloadMessages = [
        { role: 'system', content: systemPrompt },
        ...recentHistory.filter(m => m.role !== 'system').map(m => ({ role: m.role, content: m.content })),
        { role: 'user', content: userText }
      ];

      const getSession = async (forceRefresh = false) => {
        if (forceRefresh) {
          const { data: { session } } = await supabase.auth.refreshSession();
          return session;
        }
        const { data: { session } } = await supabase.auth.getSession();
        if (!session) {
          const { data: { session: refreshed } } = await supabase.auth.refreshSession();
          return refreshed;
        }
        return session;
      };

      const session = await getSession();

      // Chama o Groq e valida: tag [LANG] correta, texto não vazio e sem mistura de idiomas.
      // Resposta inválida (alucinação) é descartada e nunca chega ao usuário.
      const askTeacher = async (msgs: { role: string; content: string }[]): Promise<string | null> => {
        const response = await fetch('/api/ai-proxy', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${session?.access_token}`
          },
          body: JSON.stringify({
            model: 'llama-3.3-70b-specdec',
            source: 'active-voice',
            messages: msgs,
            max_tokens: 150
          })
        });

        if (!response.ok) {
          if (response.status === 403) {
             setShowLimitModal(true);
             setIsListening(false);
             setVoiceState('inactive');
             throw new Error('Limite de mensagens atingido.');
          }
          let errMessage = 'Erro inesperado da API.';
          try {
            const errData = await response.json();
            errMessage = errData.error?.message || errData.error || errData.message || errMessage;
          } catch(e) {}
          throw new Error(errMessage);
        }

        const data = await response.json();
        const raw: string = data.choices?.[0]?.message?.content || '';
        const tags = [...raw.matchAll(/\[LANG:\s*([a-zA-Z-]+)\]/gi)].map(m => m[1].toLowerCase().split('-')[0]);
        const text = raw.replace(/\[LANG:\s*[a-zA-Z-]+\]/gi, '').trim();
        const valid = text.length > 0
          && tags.length > 0
          && tags.every(c => c === practiceCode)
          && !looksMixed(text, practiceCode, nativeCode)
          && !looksMixed(text, practiceCode, appLang);
        if (!valid) console.warn('[Voz Ativa] Resposta descartada (idioma/formato inválido):', raw);
        return valid ? text : null;
      };

      let reply = await askTeacher(payloadMessages);
      if (!reply) {
        // uma nova tentativa, reforçando a regra
        reply = await askTeacher([
          ...payloadMessages,
          { role: 'system', content: `LEMBRETE: responda SOMENTE em ${practiceLangName}, começando com [LANG:${practiceCode}]. Nenhuma palavra em outro idioma.` }
        ]);
      }
      if (!reply) throw new Error('invalid-reply');

      const finalLang = practiceLang;
      const cleanText = reply;

      const newMsg = {
        id: Math.random().toString(36).substr(2, 9),
        role: 'assistant' as const,
        content: cleanText,
        lang: finalLang
      };
      
      setMessages(prev => [...prev, newMsg]);
      if (!isMutedRef.current) playTTS(cleanText, finalLang);
      
      // TRACKING AI MESSAGE
      if (currentSessionId && cleanText && cleanText.trim() !== '') {
        const generatedId = `${user?.id?.substring(0, 8) || 'msg'}-${Math.random().toString(36).substring(2, 10)}-${Date.now().toString(36)}`;
        dbManager.from('active_voice_messages', user?.id).insert({
          id: generatedId,
          session_id: currentSessionId,
          user_id: user?.id,
          role: 'assistant',
          content: cleanText,
          text: cleanText,
          lang: finalLang,
          created_at: new Date().toISOString()
        }).catch(() => {});
      }

      // STATS: Incrementar uso
      if (user?.id) {
        const langCode2Letters = practiceLang ? practiceLang.split('-')[0] : 'all';
        statsService.increment('pronunciation_count', user.id, 1, { langCode: langCode2Letters });
        statsService.increment('active_voice_duration', user.id, 20, { langCode: langCode2Letters }); // Local stat
        
        // Record unique words seen in assistant message
        import('../services/uniqueWordsService').then(({ uniqueWordsService }) => {
          uniqueWordsService.recordExerciseCompletion(
            user.id,
            langCode2Letters,
            'active-voice',
            newMsg.id,
            cleanText
          );
        });
        
        // Registrar sessão no D1
        dbManager.execute({
          action: 'increment_voice_session' as any,
          table: 'active_voice_sessions',
          data: { seconds: 20, language_spoken: langCode2Letters, session_id: currentSessionId },
          user_id: user.id
        }).catch(() => {});
        
        refreshActiveVoice();
      }
    } catch (err: any) {
      console.error('[Voz Ativa] Erro IA:', err);
      if (err?.message !== 'Limite de mensagens atingido.') {
        setMessages(prev => [...prev, { role: 'system', content: TEACHER_AWAY }]);
      }
    } finally {
      setVoiceState('inactive');
    }
  };

  return (
    <div className="min-h-full flex flex-col bg-tint-stone/40 relative">
      <style>{`
        .voice-dropdown-scrollbar::-webkit-scrollbar {
          width: 5px;
        }
        .voice-dropdown-scrollbar::-webkit-scrollbar-track {
          background: transparent;
        }
        .voice-dropdown-scrollbar::-webkit-scrollbar-thumb {
          background: rgba(148, 163, 184, 0.2);
          border-radius: 10px;
        }
        .dark .voice-dropdown-scrollbar::-webkit-scrollbar-thumb {
          background: rgba(255, 255, 255, 0.1);
        }
        .chat-container::-webkit-scrollbar { width: 4px; }
        .chat-container::-webkit-scrollbar-thumb { 
          background: rgba(100, 116, 139, 0.2);
          border-radius: 10px;
        }
      `}</style>

      {/* Header */}
      <div className="sticky top-0 p-6 md:p-8 flex items-center justify-between z-50 bg-tint-stone/90 backdrop-blur-md">
        <AppBackButton
          onClick={() => {
            window.speechSynthesis.cancel();
            goBackOr(navigate, '/active-voice');
          }}
          fallbackUrl="/active-voice"
        />
        {practiceLang && (
          <button
            type="button"
            onClick={toggleMute}
            title={isMuted ? t('vozativa2.unmute', 'Ativar a voz do professor') : t('vozativa2.mute', 'Silenciar o professor')}
            aria-label={isMuted ? t('vozativa2.unmute', 'Ativar a voz do professor') : t('vozativa2.mute', 'Silenciar o professor')}
            aria-pressed={isMuted}
            className={`w-10 h-10 rounded-full flex items-center justify-center border transition-colors cursor-pointer ${
              isMuted
                ? 'bg-tint-rose text-bad border-tint-rose'
                : 'bg-surface text-ink border-black/[0.06] dark:border-white/[0.06] hover:bg-tint-stone'
            }`}
          >
            {isMuted ? <VolumeX size={18} strokeWidth={1.75} /> : <Volume2 size={18} strokeWidth={1.75} />}
          </button>
        )}
      </div>

      <AnimatePresence mode="wait">
        {!practiceLang ? (
          <motion.div
            key="loading"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="flex-1 flex flex-col items-center justify-center p-6 space-y-12 relative"
          >
            {deckId === 'new' ? (
              <>
                <div className="text-center space-y-3">
                  <h1 className="text-4xl md:text-5xl font-medium text-ink tracking-[-0.03em]">{t('vozativa.voiceConfig', 'CONFIGURAÇÕES DE VOZ')}</h1>
                  <p className="text-muted font-normal uppercase font-mono tracking-[0.14em] text-[10px]">{t('vozativa.prepareToTalk', 'PREPARE-SE PARA CONVERSAR COM NOSSA IA')}</p>
                </div>

                <div className="w-full max-w-md space-y-6">
                  {/* Native Language Select */}
                  <div className="space-y-2">
                    <label className="text-[10px] font-normal uppercase font-mono tracking-[0.14em] text-muted ml-4">{t('vozativa.myLanguage', 'MEU IDIOMA')}</label>
                    <div className="relative" ref={nativeRef}>
                      <button
                        onClick={() => { setIsNativeOpen(!isNativeOpen); setIsPracticeOpen(false); }}
                        className="w-full bg-surface dark:bg-surface border border-black/[0.06] dark:border-white/[0.06] p-5 rounded-[24px] flex items-center justify-between hover:border-primary transition-all"
                      >
                        <div className="flex items-center gap-3">
                          <span className="text-lg">{ALL_LANGUAGES.find(l => l.id === nativeLang)?.flag}</span>
                          <span className="font-bold dark:text-ink">{getLanguageName(nativeLang, t)}</span>
                        </div>
                        <ChevronDown size={20} className={`text-muted transition-transform ${isNativeOpen ? 'rotate-180' : ''}`} />
                      </button>
                      {isNativeOpen && (
                        <div className="absolute top-full left-0 right-0 mt-2 bg-surface dark:bg-surface border border-black/[0.06] dark:border-white/[0.06] rounded-[24px] rounded-b-[2.5rem] shadow-[0_24px_60px_-24px_rgba(0,0,0,0.35)] z-50 overflow-hidden">
                          <div className="max-h-64 overflow-y-auto voice-dropdown-scrollbar pb-2">
                            {ALL_LANGUAGES.map(lang => (
                              <div
                                key={lang.id}
                                onClick={() => { setNativeLang(lang.id); setIsNativeOpen(false); }}
                                className="p-4 flex items-center gap-3 hover:bg-tint-stone/40 cursor-pointer transition-colors"
                              >
                                <span>{lang.flag}</span>
                                <span className="font-bold text-sm dark:text-ink">{getLanguageName(lang.id, t)}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Practice Language Select */}
                  <div className="space-y-2">
                    <label className="text-[10px] font-normal uppercase font-mono tracking-[0.14em] text-muted ml-4">{t('vozativa.wantToPractice', 'QUERO PRATICAR')}</label>
                    <div className="relative" ref={practiceRef}>
                      <button
                        onClick={() => { setIsPracticeOpen(!isPracticeOpen); setIsNativeOpen(false); }}
                        className="w-full bg-surface dark:bg-surface border border-black/[0.06] dark:border-white/[0.06] p-5 rounded-[24px] flex items-center justify-between hover:border-primary transition-all"
                      >
                        <div className="flex items-center gap-3">
                          <span className="text-lg">{ALL_LANGUAGES.find(l => l.id === setupPracticeLang)?.flag}</span>
                          <span className="font-bold dark:text-ink">{getLanguageName(setupPracticeLang || 'en-US', t)}</span>
                        </div>
                        <ChevronDown size={20} className={`text-muted transition-transform ${isPracticeOpen ? 'rotate-180' : ''}`} />
                      </button>
                      {isPracticeOpen && (
                        <div className="absolute top-full left-0 right-0 mt-2 bg-surface dark:bg-surface border border-black/[0.06] dark:border-white/[0.06] rounded-[24px] rounded-b-[2.5rem] shadow-[0_24px_60px_-24px_rgba(0,0,0,0.35)] z-50 overflow-hidden">
                          <div className="max-h-64 overflow-y-auto voice-dropdown-scrollbar pb-2">
                            {ALL_LANGUAGES.map(lang => (
                              <div
                                key={lang.id}
                                onClick={() => { setSetupPracticeLang(lang.id); setIsPracticeOpen(false); }}
                                className="p-4 flex items-center gap-3 hover:bg-tint-stone/40 cursor-pointer transition-colors"
                              >
                                <span>{lang.flag}</span>
                                <span className="font-bold text-sm dark:text-ink">{getLanguageName(lang.id, t)}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                <button
                  id="tour-vozativa-start"
                  onClick={handleStartPractice}
                  className="w-full max-w-md py-6 bg-ok hover:bg-ok text-white rounded-[24px] font-medium text-sm shadow-[#7F8A55]/40 hover:scale-[1.02] active:scale-95 transition-all"
                >
                  {t('vozativa.startPractice', 'Iniciar Prática')}
                </button>
              </>
            ) : (
              <Loader2 className="w-8 h-8 animate-spin text-tintd-sky" />
            )}
          </motion.div>
        ) : (
          <motion.div
            key="chat"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="flex-1 flex flex-col max-w-2xl mx-auto w-full overflow-hidden"
          >
            {/* Aviso de IA Local removido */}
            
            {/* 2. INTERFACE DE CHAT (Lista de Mensagens) */}
            <div className="flex justify-end px-6 pt-4 pb-0">
               <button
                  onClick={() => {
                     setMessages([]);
                     setHasMore(true);
                     fetchHistory(0);
                  }}
                  className="flex items-center gap-2 px-4 py-2 bg-tint-stone/60 hover:bg-tint-stone text-muted font-medium text-[10px] rounded-2xl transition-colors border border-black/[0.06]"
                  title="Atualizar Conversa"
               >
                  <RotateCw size={14} className={isLoadingMore ? "animate-spin" : ""} />
                  {t('vozativa.refresh', 'Atualizar')}
               </button>
            </div>
            
            <div 
              className="flex-1 overflow-y-auto chat-container p-6 space-y-4"
              ref={chatContainerRef}
            >
              {hasMore && !isLoadingMore && messages.length > 0 && (
                <div className="flex justify-center py-2">
                  <button 
                    onClick={loadMoreMessages}
                    className="px-6 py-3 bg-tint-stone/60 hover:bg-tint-stone text-muted font-medium text-[10px] rounded-2xl transition-colors border border-black/[0.06]"
                  >
                    {t('vozativa.loadOlderMessages', 'Carregar mensagens antigas...')}
                  </button>
                </div>
              )}
              {isLoadingMore && (
                <div className="flex justify-center py-4">
                  <Loader2 className="w-5 h-5 text-tintd-lavender animate-spin" />
                </div>
              )}
              {messages.map((msg, i) => {
                if (msg.role === 'system') {
                  const away = msg.content === TEACHER_AWAY;
                  return (
                    <motion.div key={i} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="flex justify-center">
                      <div className="bg-tint-butter/60 text-ink rounded-[20px] px-5 py-4 text-center max-w-[85%] flex flex-col items-center gap-1.5">
                        {away && <Coffee size={20} strokeWidth={1.6} className="text-warn" />}
                        <p className="text-sm font-medium leading-relaxed">{away ? t('vozativa2.teacherAwayTitle', 'O Professor saiu da sala pra buscar um café, mas já volta!') : msg.content}</p>
                        {away && <p className="text-xs text-muted">{t('vozativa2.teacherAwaySub', '(Em manutenção.)')}</p>}
                      </div>
                    </motion.div>
                  );
                }
                const isUser = msg.role === 'user';
                const key = msg.id || `idx-${i}`;
                const tr = msgTranslations[key];
                return (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, y: 10, scale: 0.95 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    className={`flex items-end gap-2 ${isUser ? 'justify-end' : 'justify-start'}`}
                  >
                    <div className={`max-w-[80%] px-4 py-3 rounded-[20px] ${isUser
                        ? 'bg-ink text-surface rounded-br-md'
                        : 'bg-surface border border-black/[0.06] dark:border-white/[0.06] rounded-bl-md'}
                    `}>
                      <p className="font-medium leading-relaxed">{renderClickableText(msg.content, msg.lang)}</p>
                      {tr?.text && (
                        <p className={`mt-2 pt-2 border-t text-sm leading-relaxed ${isUser ? 'border-surface/20 text-surface/80' : 'border-black/[0.06] dark:border-white/[0.06] text-muted'}`}>
                          {tr.text}
                        </p>
                      )}
                    </div>

                    {/* Ações do balão: ouvir e traduzir */}
                    <div className={`flex flex-col gap-1 shrink-0 ${isUser ? 'order-first' : ''}`}>
                      <button
                        type="button"
                        onClick={() => togglePlay(key, msg.content, isUser ? (msg.lang || nativeLang) : (msg.lang || practiceLang))}
                        className={`w-8 h-8 rounded-full flex items-center justify-center border transition-colors cursor-pointer ${playingKey === key ? 'bg-ink text-surface border-ink' : 'bg-tint-stone/60 text-muted hover:text-ink border-black/[0.06] dark:border-white/[0.06]'}`}
                        title={playingKey === key ? t('vozativa2.stop', 'Parar áudio') : t('vozativa2.play', 'Ouvir mensagem')}
                        aria-label={playingKey === key ? t('vozativa2.stop', 'Parar áudio') : t('vozativa2.play', 'Ouvir mensagem')}
                      >
                        {playingKey === key ? <Square size={13} fill="currentColor" /> : <Volume2 size={15} />}
                      </button>
                      <button
                        type="button"
                        onClick={() => toggleMessageTranslation(key, msg.content, isUser ? (msg.lang || nativeLang) : msg.lang)}
                        className={`w-8 h-8 rounded-full flex items-center justify-center border transition-colors cursor-pointer ${tr?.text ? 'bg-ink text-surface border-ink' : 'bg-tint-stone/60 text-muted hover:text-ink border-black/[0.06] dark:border-white/[0.06]'}`}
                        title={tr?.text ? t('vozativa2.hideTranslation', 'Ocultar tradução') : tr?.loading ? t('vozativa2.translating', 'Traduzindo...') : t('vozativa2.translate', 'Traduzir mensagem')}
                        aria-label={t('vozativa2.translate', 'Traduzir mensagem')}
                      >
                        {tr?.loading ? <Loader2 size={15} className="animate-spin" /> : <Languages size={15} />}
                      </button>
                    </div>
                  </motion.div>
                );
              })}
              
              {voiceState === 'processing' && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="flex justify-start"
                >
                  <div className="bg-surface border border-black/[0.06] dark:border-white/[0.06] p-4 rounded-[24px] rounded-bl-none flex items-center gap-3">
                    <Loader2 className="w-5 h-5 text-tintd-lavender animate-spin" />
                    <span className="text-[10px] font-normal uppercase font-mono tracking-[0.14em] text-muted">{t('vozativa.processing')}</span>
                  </div>
                </motion.div>
              )}
              {/* Anchor for Auto-Scroll */}
              <div ref={messagesEndRef} />
            </div>

            {/* Barra Inferior de Entrada */}
            <div className="p-4 bg-surface border-t border-black/[0.06] dark:border-white/[0.06] flex flex-col gap-2 shrink-0">
              {!isPro && (
                <div className="text-[10px] font-normal uppercase font-mono tracking-[0.14em] text-muted text-center">
                  {t('vozativa.dailyLimit', 'Mensagens diárias gratuitas')}: {messageCount}/{FREE_LIMIT}
                </div>
              )}
              <div className="flex items-end gap-3" id="tour-vozativa-input">
                <textarea
                  value={textInput}
                  onChange={(e) => setTextInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      handleSendMessage();
                    }
                  }}
                  placeholder={(!isPro && messageCount >= FREE_LIMIT) ? t('vozativa.limitReachedPlaceholder', 'Limite diário atingido...') : t('vozativa.typePlaceholder', 'Digite sua mensagem aqui...')}
                  disabled={!isPro && messageCount >= FREE_LIMIT}
                  className="flex-1 max-h-32 min-h-[48px] bg-tint-stone/40 rounded-[1.5rem] px-5 py-3.5 resize-none outline-none text-sm font-medium dark:text-ink border-none focus:ring-2 focus:ring-primary/20 transition-all disabled:opacity-50"
                  rows={1}
                />
                <button
                  onClick={handleSendMessage}
                  disabled={!textInput.trim() || voiceState === 'processing' || (!isPro && messageCount >= FREE_LIMIT)}
                  className="w-12 h-12 bg-primary text-slate-900 rounded-[1.5rem] flex items-center justify-center shrink-0 disabled:opacity-50 hover:bg-primary/90 hover:scale-[1.02] active:scale-95 transition-all"
                  title={t('common.send', 'Enviar')}
                >
                  <MessageSquare className="w-5 h-5" />
                </button>
                <div className="relative">
                  <button
                    onClick={toggleMic}
                    disabled={voiceState === 'processing' || (!isPro && messageCount >= FREE_LIMIT)}
                    className={`relative w-12 h-12 rounded-[1.5rem] flex items-center justify-center shrink-0 border transition-all overflow-hidden ${
                      isListening 
                        ? 'bg-tint-rose dark:bg-tint-rose/30 text-bad border-tint-rose dark:border-tint-rose' 
                        : 'bg-tint-stone/60 text-muted hover:bg-tint-stone border-black/[0.06] disabled:opacity-50'
                    }`}
                    title={isListening ? t('vozativa.stopRecording', 'Parar Gravação') : t('vozativa.speakMic', 'Falar no Microfone')}
                  >
                    <Mic size={20} className={isListening ? 'animate-pulse' : ''} />
                    
                    {/* Visual Progress Ring */}
                    {isListening && (
                      <svg className="absolute inset-0 w-full h-full pointer-events-none" viewBox="0 0 100 100" style={{ transform: 'rotate(-90deg)' }}>
                        <circle
                          cx="50"
                          cy="50"
                          r="48"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="4"
                          className="text-bad/30"
                        />
                        <circle
                          cx="50"
                          cy="50"
                          r="48"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="4"
                          className="text-bad"
                          strokeDasharray="301.59"
                          strokeDashoffset={301.59 - (301.59 * (recordingTime / 15))}
                          strokeLinecap="round"
                          style={{ transition: 'stroke-dashoffset 1s linear' }}
                        />
                      </svg>
                    )}
                  </button>
                  {isListening && (
                    <div className="absolute -top-2 -right-2 bg-bad text-surface text-[9px] font-semibold w-5 h-5 rounded-full flex items-center justify-center border border-black/[0.06] dark:border-white/[0.06] dark:border-white/[0.06] z-10 animate-bounce">
                      {recordingTime}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <OfflineTranslatorPromptModal open={showOfflinePrompt} onClose={() => setShowOfflinePrompt(false)} />

      {/* Modal de Limite Free */}
      <AnimatePresence>
        {showLimitModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="absolute inset-0 bg-black/30 backdrop-blur-sm"
              onClick={() => setShowLimitModal(false)}
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="bg-surface rounded-3xl overflow-hidden max-w-sm w-full relative z-10 p-6 flex flex-col items-center text-center"
            >
              <div className="w-16 h-16 bg-tint-butter dark:bg-tint-butter/30 text-warn rounded-full flex items-center justify-center mb-4">
                <Sparkles className="w-8 h-8" />
              </div>
              <h2 className="text-xl font-bold text-ink mb-2">{t('vozativa.limitReached')}</h2>
              <p className="text-muted text-sm mb-6">
                {t('vozativa.limitDescription', 'Você atingiu o limite de conversas diárias. Faça o upgrade para o LibriTui Premium para ter conversas ilimitadas com o Professor!')}
              </p>
              
              <button
                onClick={() => {
                  window.location.href = 'https://www.libritui.com/settings/upgrade';
                }}
                className="w-full bg-primary hover:bg-primary/90 text-slate-900 font-bold py-3 px-6 rounded-full transition-all mb-3"
              >
                {t('vozativa.viewPremium', 'Ver Planos Premium')}
              </button>
              
              <button
                onClick={() => setShowLimitModal(false)}
                className="w-full bg-tint-stone/60 hover:bg-tint-stone text-muted font-bold py-3 px-6 rounded-full transition-all"
              >
                {t('common.back', 'Voltar')}
              </button>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};


export default VozAtivaPage;
